import { usePagination } from "@/common/hooks/usePagination";
import { useSearch } from "@/common/hooks/useSearch";
import { useAuth } from "@/context/auth-context";
import { useGetTransactions } from "@/features/transactions/useGetTransactions";
import { useState } from "react";

import SearchBox from "@/components/_module/searchBox";
import PageLoader from "@/components/ui/PageLoader";
import ActivityModal from "./_components/ActivityModal";
import TransactionList from "./_components/TransactionList";

import { useSort } from "@/common/hooks/useSort";
import { Button } from "@/components/ui/button/button";
import { useExportTransactions } from "@/features/transactions/useExportTransactions";
import { useLoaderData } from "@tanstack/react-router";
import { SlidersHorizontal } from "lucide-react";
import { getTimePeriod } from "./_components/utils";

type TransactionType = "deposit" | "withdrawal";

const ActivityPage = () => {
	const { user } = useAuth();

	const pagination = usePagination({
		initialPage: 1,
		initialPerPage: 15,
	});

	const { location_id } = useLoaderData({ from: "__root__" });

	const search = useSearch({
		onSearchChange: () => pagination.resetToFirstPage(),
	});

	const [isModalOpen, setModalOpen] = useState(false);
	const [transactionType, setTransactionType] = useState<
		TransactionType | undefined
	>(undefined);
	const [selectedMonth, setSelectedMonth] = useState<Date | undefined>(
		undefined,
	);

	const {
		sortBy,
		sortOrder,
		handleSortFieldChange,
		handleSortOrderChange,
		resetSort,
	} = useSort({ initialSortBy: "date", initialSortOrder: "desc" });

	const handleApply = () => {
		pagination.resetToFirstPage();
		setModalOpen(false);
	};

	const handleReset = () => {
		setTransactionType(undefined); // no selection
		setSelectedMonth(undefined);
		resetSort();
		search.clearSearch();
	};

	const period = getTimePeriod(selectedMonth);
	const exportMutation = useExportTransactions();

	const handleExport = () => {
		exportMutation.mutate({
			locationId: location_id,
			guestId: user.id,
			transaction_type_id: transactionType
				? TRANSACTION_TYPE_MAP[transactionType]
				: undefined,
			sort_by: sortBy === "date" ? "date" : "amount",
			sort_order: sortOrder,
			period,
		});
	};

	const TRANSACTION_TYPE_MAP: Record<string, number> = {
		deposit: 1,
		withdrawal: 14,
	};

	const query = useGetTransactions({
		locationId: location_id,
		search: search.search,
		guestId: user.id,
		transaction_type_id: transactionType
			? TRANSACTION_TYPE_MAP[transactionType]
			: undefined,
		sort_by: sortBy === "date" ? "date" : "amount",
		sort_order: sortOrder,
		page: pagination.currentPage,
		per_page: pagination.perPage,
		period,
	});

	const handleMonthSelect = (date: Date) => {
		setSelectedMonth(date);
	};

	return (
		<div className="px-5 w-full">
			<h1 className="text-2xl font-bold text-textDark">Transactions</h1>

			<div className="flex items-center w-full gap-2 mt-3 mb-7">
				<div className="flex-1">
					<SearchBox onSearch={search.handleSearchChange} />
				</div>
				<Button
					variant="unstyled"
					size="compact"
					className="cursor-pointer"
					onClick={() => setModalOpen(true)}
				>
					<SlidersHorizontal className="stroke-primary-600" />
				</Button>
			</div>

			<PageLoader query={query}>
				{(data) => (
					<TransactionList
						data={data?.data || []}
						totalRows={data?.total || 0}
						currentPage={pagination.currentPage}
						perPage={pagination.perPage}
						onPageChange={pagination.handlePageChange}
					/>
				)}
			</PageLoader>

			<ActivityModal
				isOpen={isModalOpen}
				onClose={() => setModalOpen(false)}
				transactionType={transactionType}
				setTransactionType={setTransactionType}
				sortBy={sortBy}
				handleSortFieldChange={handleSortFieldChange}
				sortOrder={sortOrder}
				handleSortOrderChange={handleSortOrderChange}
				onExport={handleExport}
				onApply={handleApply}
				onReset={handleReset}
				selectedMonth={selectedMonth}
				onMonthSelect={handleMonthSelect}
			/>
		</div>
	);
};

export default ActivityPage;
